import type { AgentOnboardingProgress } from "@/api/temp/AgentEstateService";
import { finishedAtOf } from "@/pages/AgentEstate/onboarding/phases";
import type { EstateExperienceResult } from "@/pages/TrustAIExecutiveBoard/estateExperience";
import { EstateReadiness } from "@/pages/TrustAIExecutiveBoard/types";

/**
 * The Security Overview dashboard's lifecycle, and what each state is allowed
 * to claim.
 *
 * One page, five states. The board is the same dashboard throughout — it does
 * not swap in an onboarding application and then swap it back out. What changes
 * is which sections can tell the truth yet:
 *
 *   ZERO_DAY    nothing connected. Analytics would all read 0, and "0
 *               violations" reads as "you are safe" when the truth is "nothing
 *               has been looked at". So the page asks a different question:
 *               what should I connect?
 *   DISCOVERING a platform is connected and the estate is filling in. Counts
 *               are real but partial, so a zero needs saying-so rather than
 *               presenting as an estate total.
 *   DISCOVERED  the estate is known; nothing has been judged about it. Explore
 *               freely, but governance analytics stay withheld.
 *   EVALUATING  access paths are being evaluated. Findings appear as they land.
 *   ACTIVE      the full Day-Two dashboard, exactly as it has always been.
 *
 * The lifecycle describes the *run*. What the tenant already has - registered
 * data platforms, onboarded agents - is a separate question answered by
 * `resolveEstateExperience`, and at day zero it is what decides between the
 * welcome composition and a data-first one. Without that, a tenant with
 * registered data platforms and no agent registry would be told it has nothing.
 *
 * Deriving the state in one place is what keeps the count checks out of the
 * JSX. Sections read a capability flag, never a raw number and never a
 * lifecycle string comparison.
 */
export type DashboardLifecycleState =
  | "ZERO_DAY"
  | "DISCOVERING"
  | "DISCOVERED"
  | "EVALUATING"
  | "ACTIVE";

export const getDashboardLifecycleState = (
  readiness: EstateReadiness,
): DashboardLifecycleState => {
  if (readiness.evaluationStatus === "COMPLETED") return "ACTIVE";
  if (readiness.evaluationStatus === "RUNNING") return "EVALUATING";
  if (readiness.discoveryStatus === "COMPLETED") return "DISCOVERED";
  // A connected platform whose discovery has not started yet still belongs
  // here rather than at day zero: the reader has done the one thing the
  // Zero-Day hero asked of them, and showing them that same ask again reads
  // as the product not having noticed.
  if (
    readiness.discoveryStatus === "RUNNING" ||
    readiness.connectedPlatforms > 0
  )
    return "DISCOVERING";
  return "ZERO_DAY";
};

/**
 * Which sections the board renders, keyed by intent rather than by state.
 *
 * Every flag answers "can this section say something true right now?". A
 * section that would have to invent a comparison, or present an unknown as a
 * zero, is absent — not rendered empty, and never rendered reassuring.
 */
export interface DashboardCapabilities {
  /** Nothing is searchable before discovery finds anything. */
  showSearch: boolean;
  /**
   * Inventory is still loading, failed, or restricted at day zero: neither the
   * welcome nor a data-first composition can be claimed yet.
   */
  showInventoryPending: boolean;
  showSetupProgress: boolean;
  /**
   * The onboarding run's own progress - one block for both halves of a chained
   * run, so a reader watching it never sees it disappear and reappear.
   */
  showOnboardingProgress: boolean;
  showGovernanceReadiness: boolean;
  showEvaluationProgress: boolean;
  showRequiresAttention: boolean;
}

export const dashboardCapabilities = (
  state: DashboardLifecycleState,
  readiness: EstateReadiness,
  experience?: EstateExperienceResult,
): DashboardCapabilities => {
  const isZeroDay = state === "ZERO_DAY";
  // Only day zero is ever re-read through inventory: once a run exists the
  // lifecycle already describes an estate. An unresolved experience at day zero
  // is pending, not empty.
  const dayZero = isZeroDay ? (experience?.experience ?? "EMPTY") : undefined;
  // A run started from the onboarding wizard carries on into evaluation by
  // itself, so its two halves are one block and there is never a "now evaluate
  // it" prompt in the middle.
  const isChained = readiness.chainedOnboarding;

  return {
    // Search indexes discovered agents and data sources; a data-first tenant
    // has no discovery behind it yet.
    showSearch: !isZeroDay,
    showInventoryPending: dayZero === "UNRESOLVED",
    // The hero shrinks to a progress summary once the first connection lands,
    // and disappears entirely once there are governance findings to read. A
    // chained run already leads with its own progress block, so a second
    // summary of the same run would only repeat it.
    showSetupProgress:
      (state === "DISCOVERING" && !isChained) || state === "DISCOVERED",
    showOnboardingProgress:
      state === "DISCOVERING" || (state === "EVALUATING" && isChained),
    // Never reached by a chained run: its discovery rolls straight into
    // evaluation, so the "now evaluate it" prompt has nothing to ask for.
    showGovernanceReadiness: state === "DISCOVERED",
    showEvaluationProgress: state === "EVALUATING" && !isChained,
    // Findings land progressively, so they show while evaluation runs.
    showRequiresAttention: state === "EVALUATING" || state === "ACTIVE",
  };
};

/**
 * The same shape for live mode, where there is no readiness run: only the
 * estate search, once the inventory holds anything. Every notice that reads a
 * mock-only service stays off, so the page composes from `capabilities` alike
 * in both modes.
 */
export const liveCapabilities = (
  hasInventory: boolean,
): DashboardCapabilities => ({
  // Live mode has no day-zero lifecycle: the estate is past day zero once the
  // inventory reports anything at all to find.
  showSearch: hasInventory,
  showInventoryPending: false,
  showSetupProgress: false,
  showOnboardingProgress: false,
  showGovernanceReadiness: false,
  showEvaluationProgress: false,
  showRequiresAttention: false,
});

/** How long a finished onboarding job keeps announcing itself on the board. */
export const ONBOARDING_OUTCOME_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Whether a *finished* onboarding job still earns the progress card.
 *
 * A failed job always does: it needs attention. A completed one does for a day
 * after it finishes, so the card does not resurface every session for an
 * environment onboarded long ago. A job that finished without importing any
 * agents has its own explanatory notice, so it never gets a success card.
 * Running jobs are not decided here - `showOnboardingProgress` owns those.
 */
export const showOnboardingOutcome = (
  progress: AgentOnboardingProgress | undefined,
  now = Date.now(),
): boolean => {
  if (!progress) return false;
  if (progress.lifecycle === "FAILED") return true;
  if (progress.lifecycle !== "COMPLETE" || progress.discoveredCount === 0)
    return false;
  return now - finishedAtOf(progress) < ONBOARDING_OUTCOME_WINDOW_MS;
};
